import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { setAppsMarkets, setSelectedApp } from "../../../../state/ducks";
import { dispatch, getState } from "../../../../state/store";
import { selectAppsMarkets } from "../../../../state/selectors";

import { appsService, log, notificationService, t } from "../../../../services";
import { strCode } from "../../../../consts";
import { responseUtil } from "../../../../utils";

import { Heading5, Modal } from "../../../../components";
import MarketFeaturesModalTable from "./MarketFeaturesModalTable";
import MarketFeaturesModalControlButtons from "./MarketFeaturesModalControlButtons";

const MarketFeaturesModal = ({ isActive, setActive, selectedMarket }) => {
  const selectedApp = useSelector((store) => store.apps?.selectedApp);
  const defaultFeatures = useSelector((store) => store.apps?.selectedApp.defaultSettings);
  const marketConfigs = useSelector((store) => store.apps?.selectedApp?.marketConfigs);
  const allMarkets = useSelector((store) => store.markets?.markets);

  const [marketFeaturesModalData, setMarketFeaturesModalData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const selectedMarketName = useMemo(
    () => allMarkets?.find((market) => market.market === selectedMarket?.market)?.name || "",
    [allMarkets, selectedMarket]
  );

  const initialMarketFeatures = useMemo(() => {
    if (!selectedMarket) {
      return [];
    }

    return selectedMarket.settings.length ? selectedMarket.settings : defaultFeatures;
  }, [defaultFeatures, selectedMarket]);

  useEffect(() => {
    if (initialMarketFeatures.length && !marketFeaturesModalData.length) {
      setMarketFeaturesModalData(initialMarketFeatures);
    }
  }, [initialMarketFeatures.length]);

  const onModalClose = () => {
    setMarketFeaturesModalData([]);
    setActive();
  };

  const onToggleValue = (featureName) => {
    setMarketFeaturesModalData(
      marketFeaturesModalData.map((feature) => {
        if (feature.settingName === featureName) {
          return {
            ...feature,
            value: !feature.value
          };
        }

        return feature;
      })
    );
  };

  const onSave = async () => {
    if (isLoading) return;

    try {
      setIsLoading(true);

      const res = await appsService.updateAppTypeMarketConfig({
        ...selectedMarket,
        settings: marketFeaturesModalData
      });

      if (responseUtil.isResponseSuccessful(res)) {
        const newSelectedApp = {
          ...selectedApp,
          marketConfigs: marketConfigs?.map((config) => {
            if (config.market === selectedMarket.market) {
              return {
                ...config,
                settings: marketFeaturesModalData
              };
            }

            return config;
          })
        };

        const appsMarkets = selectAppsMarkets()(getState());

        dispatch(setSelectedApp(newSelectedApp));
        dispatch(
          setAppsMarkets({
            ...appsMarkets,
            [selectedApp.appType]: (appsMarkets[selectedApp.appType]
              ? appsMarkets[selectedApp.appType]
              : []
            ).map((market) => {
              if (market.market === selectedMarket.market) {
                return {
                  ...market,
                  settings: marketFeaturesModalData
                };
              }

              return market;
            })
          })
        );

        onModalClose();

        notificationService.showSuccess({
          title: t(strCode.CONFIGURATION_SAVED_TITLE),
          message: t(strCode.MARKET_CONFIGURATION_SAVED_DESCRIPTION, {
            market: selectedMarket.market
          })
        });
      }
    } catch (err) {
      log.error("MarketFeaturesModal: Failed to update market config", err);
      notificationService.showError({
        title: t(strCode.CONFIGURATION_SAVE_ERROR_TITLE),
        message: err?.response?.data?.message ? err.response.data.message : err?.toString()
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onResetToDefault = () => {
    setMarketFeaturesModalData(defaultFeatures);
  };

  return (
    <Modal
      id="market-features-modal"
      active={isActive}
      setActive={onModalClose}
      modalHeader={<Heading5>{`${selectedMarketName} ${t(strCode.SETTINGS_NAV_BTN)}`}</Heading5>}
      modalFooter={
        <MarketFeaturesModalControlButtons
          onSave={onSave}
          onResetToDefault={onResetToDefault}
          onCancel={onModalClose}
          loading={isLoading}
        />
      }>
      <MarketFeaturesModalTable features={marketFeaturesModalData} onToggleValue={onToggleValue} />
    </Modal>
  );
};

MarketFeaturesModal.propTypes = {
  isActive: PropTypes.bool.isRequired,
  setActive: PropTypes.func.isRequired,
  selectedMarket: PropTypes.shape({
    appType: PropTypes.string.isRequired,
    market: PropTypes.string.isRequired,
    settings: PropTypes.arrayOf(
      PropTypes.shape({
        code: PropTypes.string.isRequired,
        settingName: PropTypes.string.isRequired,
        value: PropTypes.bool.isRequired
      })
    ).isRequired
  }).isRequired
};

export default MarketFeaturesModal;
